import {
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Menu,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { writeData } from "../../../../api";
import { insertPageBlock } from "../../../../api/page";
import {
  useComponent,
  useComponents,
  useComponentSearch,
} from "../../../../hook/component";
import { useAddImplementations } from "../../../../hook/implementation";
import { useMenu, UseMenuProps } from "../../../../hook/menu";
import { useSelectedNotebookPageIdMap } from "../../../../hook/page";
import { useInsertBlock } from "../../../../hook/page/block";
import {
  useFetchAllProtocolsListener,
  useProtocol,
  useProtocols,
} from "../../../../hook/protocol";
import { newImplementation } from "../../../../types/implementation";
import { newimplementationBlock } from "../../../../types/notebook/note/block/implementation";
import { ComponentSelectMenu } from "../../component";
import { ProtocolSelectMenu } from "../../protocol";
import { Protocol } from "../../../../types/protocol";
import { Component } from "../../../../types/component";

export const AddImplementationMenu: React.FC<
  UseMenuProps & { blockIndex: number }
> = ({ blockIndex, ...useMenuProps }) => {
  const { onClose } = useMenuProps;
  const [formState, setFormState] = useState<{
    selectedProto: Protocol | undefined;
    selectedComp: Component | undefined;
    protoRequired: boolean;
    compRequired: boolean;
  }>({
    selectedProto: undefined,
    selectedComp: undefined,
    protoRequired: false,
    compRequired: false,
  });
  const { selectedProto, selectedComp, compRequired, protoRequired } =
    formState;
  const setSelectedProto = (selectedProto: Protocol) =>
    setFormState((formState) => ({ ...formState, selectedProto }));
  const setSelectedComp = (selectedComp: Component) =>
    setFormState((formState) => ({ ...formState, selectedComp }));
  const setProtoRequired = (protoRequired: boolean) =>
    setFormState((formState) => ({ ...formState, protoRequired }));
  const setCompRequired = (compRequired: boolean) =>
    setFormState((formState) => ({ ...formState, compRequired }));
  const { selectedNotebookPage } = useSelectedNotebookPageIdMap();
  const addImplementations = useAddImplementations();
  const insertBlock = useInsertBlock();

  const handleSubmit = () => {
    selectedComp === undefined ? setCompRequired(true) : setCompRequired(false);
    selectedProto === undefined
      ? setProtoRequired(true)
      : setProtoRequired(false);
    if (!selectedComp || !selectedProto) return;

    const impl = newImplementation({
      name: "Implementation",
      page: selectedNotebookPage!,
      comp: selectedComp!,
      proto: selectedProto!,
    });
    const block = newimplementationBlock({ implementation: impl, blockIndex });
    addImplementations([impl]);
    insertBlock({ blockIndex, block });
    writeData(impl);
    insertPageBlock({ "block-index": blockIndex, block });
    onClose();
    // console.log("NEW_IMPL", block);
  };
  const {
    anchorEl: anchorElCompSelect,
    handleOpen: handleOpenCompSelect,
    onClose: onCloseCompSelect,
    open: openCompSelect,
  } = useMenu();
  const {
    anchorEl: anchorElProtoSelect,
    handleOpen: handleOpenProtoSelect,
    onClose: onCloseProtoSelect,
    open: openProtoSelect,
  } = useMenu();

  return (
    <>
      <ComponentSelectMenu
        anchorEl={anchorElCompSelect}
        onChange={async (comp) => {
          setSelectedComp(comp);
        }}
        onClose={onCloseCompSelect}
        open={openCompSelect}
      />
      <ProtocolSelectMenu
        anchorEl={anchorElProtoSelect}
        onChange={async (proto) => {
          setSelectedProto(proto);
        }}
        onClose={onCloseProtoSelect}
        open={openProtoSelect}
      />
      <Menu {...useMenuProps}>
        <Box
          sx={{
            width: "20rem",
            padding: 3,
            gap: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="h5">Add Implementation</Typography>
          <Typography variant="subtitle1">
            Extend a protocol for a component
          </Typography>
          <Divider />
          <FormControl>
            <FormLabel id="add-implementation-select-protocol">
              Protocol To Extend
            </FormLabel>
            <Button onClick={handleOpenProtoSelect}>
              {selectedProto?.name ?? "Select Protocol"}
            </Button>
            {protoRequired && (
              <FormHelperText color="error">Protocol Required</FormHelperText>
            )}
          </FormControl>
          <FormControl>
            <FormLabel id="add-implementation-select-component">
              Component To Extend To
            </FormLabel>
            <Button onClick={handleOpenCompSelect}>
              {selectedComp?.name ?? "Select Component"}
            </Button>
            {compRequired && (
              <FormHelperText color="error">Component Required</FormHelperText>
            )}
          </FormControl>
          <Button onClick={() => handleSubmit()} variant="outlined">
            Add Implementation
          </Button>
        </Box>
      </Menu>
    </>
  );
};
